/**
 * useIntersectionObserver Hook
 *
 * A reusable hook for observing when an element enters the viewport.
 * Useful for infinite scrolling, lazy loading, and other intersection-based features.
 */
import { RefObject, useEffect, useRef, useState } from 'react';

interface UseIntersectionObserverOptions {
  enabled?: boolean;
  root?: Element | RefObject<Element | null> | null;
  rootMargin?: string;
  threshold?: number | Array<number>;
}

interface UseIntersectionObserverReturn {
  isIntersecting: boolean;
  ref: RefObject<HTMLDivElement | null>;
}

/**
 * Hook to observe when an element enters the viewport
 *
 * @param callback - Function to call when intersection occurs
 * @param options - IntersectionObserver options
 * @returns Object with ref to attach to element and intersection state
 *
 * @example
 * ```tsx
 * const { ref, isIntersecting } = useIntersectionObserver(
 *   () => {
 *     if (hasNextPage && !isFetchingNextPage) {
 *       fetchNextPage()
 *     }
 *   },
 *   {
 *     rootMargin: '100px',
 *     threshold: 0.1,
 *     enabled: hasNextPage && !isFetchingNextPage,
 *   }
 * )
 *
 * return <div ref={ref}>Observer target</div>
 * ```
 */
export function useIntersectionObserver(
  callback: (isIntersecting: boolean) => void,
  options: UseIntersectionObserverOptions = {},
): UseIntersectionObserverReturn {
  const { enabled = true, root = null, rootMargin = '0px', threshold = 0.1 } = options;

  const ref = useRef<HTMLDivElement>(null);
  const [isIntersecting, setIsIntersecting] = useState(false);

  useEffect(() => {
    if (!enabled || !ref.current) {
      return;
    }

    // Resolve root element from ref or direct element
    const rootElement = root && 'current' in root ? root.current : (root as Element | null);

    const observer = new IntersectionObserver(
      (entries) => {
        const entry = entries[0];
        if (!entry) {
          return;
        }

        const intersecting = entry.isIntersecting;
        setIsIntersecting(intersecting);

        if (intersecting) {
          callback(intersecting);
        }
      },
      {
        root: rootElement,
        rootMargin,
        threshold,
      },
    );

    const currentTarget = ref.current;
    if (currentTarget) {
      observer.observe(currentTarget);
    }

    return () => {
      // Disconnect observer to clean up properly
      observer.disconnect();
    };
  }, [callback, root, rootMargin, threshold, enabled]);

  return { isIntersecting, ref };
}
